import {
  Add as AddIcon,
  Article as ArticleIcon,
  Close as CloseIcon,
  ChatBubbleOutline as CommentIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  Group as GroupIcon,
  Image as ImageIcon,
  MoreVert as MoreVertIcon,
  Newspaper as NewspaperIcon,
  Public as PublicIcon,
  PublicOff as PublicOffIcon,
  Send as SendIcon,
  ShoppingBag as ShoppingBagIcon,
  Tag as TagIcon,
} from '@mui/icons-material';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import CancelButton from '../components/CancelButton';
import Loading from '../components/Loading';
import { useDietitianData } from '../hooks/useDietitianData';
import { CreatePostDto, Post, postApi } from '../services/postApi';
import { colors } from '../theme';

const ContentManagementPage: React.FC = () => {
  const { dietitianId, userEmail, showSuccess, showError } = useDietitianData({
    fetchClients: false,
    fetchAppointments: false,
    fetchDietPrograms: false,
  });
  const [dietitianName, setDietitianName] = useState('');
  const [dietitianProfileImage, setDietitianProfileImage] = useState<string>('');
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState<Post | null>(null);
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [editingComments, setEditingComments] = useState<Record<string, string>>({}); // { commentId: content }
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({}); // { postId: boolean }
  const [menuAnchor, setMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [commentMenuAnchor, setCommentMenuAnchor] = useState<{ [key: string]: HTMLElement | null }>({});

  const [formData, setFormData] = useState<CreatePostDto>({
    dietitianId: dietitianId || '',
    dietitianName: dietitianName,
    title: '',
    content: '',
    type: 'article',
    price: undefined,
    productUrl: '',
    imageUrl: '',
    tags: [],
    isPublished: true,
    visibility: 'public',
  });

  const [tagInput, setTagInput] = useState('');

  // Diyetisyen bilgilerini yükle
  useEffect(() => {
    const loadDietitianData = async () => {
      if (userEmail) {
        try {
          const { dietitianApi } = await import('../services/dietitianApi');
          const dietitianData = await dietitianApi.getDietitianByEmail(
            userEmail
          );
          const fullName = `${dietitianData.firstName} ${dietitianData.lastName}`;
          setDietitianName(fullName);
          setDietitianProfileImage(dietitianData.profileImageUrl || '');
          
          // Postlardaki diyetisyen ismini güncelle
          setPosts(prevPosts => 
            prevPosts.map(post => 
              post.dietitianId === dietitianData.id
                ? { ...post, dietitianName: fullName }
                : post
            )
          );
        } catch (error) {
          console.error('Error loading dietitian data:', error);
        }
      }
    };
    loadDietitianData();
  }, [userEmail]);

  useEffect(() => {
    if (dietitianId) {
      loadPosts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dietitianId]);

  // Diyetisyen ismi değiştiğinde postları güncelle
  useEffect(() => {
    if (dietitianName) {
      setPosts(prevPosts => 
        prevPosts.map(post => 
          post.dietitianId === dietitianId
            ? { ...post, dietitianName: dietitianName }
            : post
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dietitianName]);

  const loadPosts = async () => {
    if (!dietitianId) return;

    try {
      setLoading(true);
      const data = await postApi.getPostsByDietitianId(dietitianId);
      // Diyetisyen ismini güncelle (eğer diyetisyen ismi yüklendiyse)
      const updatedData = data.map(post => ({
        ...post,
        dietitianName: dietitianName || post.dietitianName,
      }));
      setPosts(updatedData);
    } catch (error) {
      console.error('Error loading posts:', error);
      showError('İçerikler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (post?: Post) => {
    if (post) {
      setEditingPost(post);
      setFormData({
        dietitianId: post.dietitianId,
        dietitianName: dietitianName || post.dietitianName,
        title: post.title,
        content: post.content,
        type: post.type,
        price: post.price,
        productUrl: post.productUrl || '',
        imageUrl: post.imageUrl || '',
        tags: post.tags,
        isPublished: post.isPublished,
        visibility: post.visibility || 'public',
      });
    } else {
      setEditingPost(null);
      setFormData({
        dietitianId: dietitianId || '',
        dietitianName: dietitianName,
        title: '',
        content: '',
        type: 'article',
        price: undefined,
        productUrl: '',
        imageUrl: '',
        tags: [],
        isPublished: true,
        visibility: 'public',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setTagInput('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Güncel diyetisyen ismini kullan
      const postData = {
        ...formData,
        dietitianName: dietitianName || formData.dietitianName,
      };

      if (editingPost) {
        await postApi.updatePost(editingPost.id, postData);
        showSuccess('İçerik başarıyla güncellendi');
      } else {
        await postApi.createPost(postData);
        showSuccess('İçerik başarıyla oluşturuldu');
      }
      handleCloseModal();
      loadPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      showError('İçerik kaydedilirken hata oluştu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu içeriği silmek istediğinizden emin misiniz?'))
      return;

    try {
      await postApi.deletePost(id);
      showSuccess('İçerik başarıyla silindi');
      loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      showError('İçerik silinirken hata oluştu');
    }
  };

  const handleTogglePublish = async (id: string) => {
    try {
      await postApi.togglePublish(id);
      showSuccess('Yayın durumu güncellendi');
      loadPosts();
    } catch (error) {
      console.error('Error toggling publish:', error);
      showError('Yayın durumu güncellenirken hata oluştu');
    }
  };

  const handleToggleLike = async (post: Post) => {
    if (!dietitianId) return;
    
    // Optimistic update - önce UI'ı güncelle
    const isCurrentlyLiked = post.likedBy?.includes(dietitianId) || false;
    const newLikedBy = isCurrentlyLiked
      ? post.likedBy?.filter(id => id !== dietitianId) || []
      : [...(post.likedBy || []), dietitianId];
    const newLikeCount = isCurrentlyLiked
      ? Math.max(0, (post.likeCount || 0) - 1)
      : (post.likeCount || 0) + 1;

    // UI'ı hemen güncelle
    setPosts(posts.map(p => 
      p.id === post.id 
        ? { ...p, likedBy: newLikedBy, likeCount: newLikeCount }
        : p
    ));
    
    try {
      await postApi.toggleLike(post.id, dietitianId);
      // Başarılı oldu, zaten güncelledik
    } catch (error) {
      console.error('Error toggling like:', error);
      // Hata olursa geri al
      setPosts(posts.map(p => 
        p.id === post.id 
          ? { ...p, likedBy: post.likedBy, likeCount: post.likeCount }
          : p
      ));
      showError('Beğeni işlemi başarısız');
    }
  };

  const handleAddComment = async (postId: string) => {
    if (!dietitianId || !commentInputs[postId]?.trim()) return;

    const commentContent = commentInputs[postId].trim();
    const tempCommentId = `temp-${Date.now()}`;
    
    // Optimistic update - önce UI'ı güncelle
    const newComment = {
      id: tempCommentId,
      userId: dietitianId,
      userName: dietitianName,
      content: commentContent,
      createdAt: new Date().toISOString(),
    };

    // UI'ı hemen güncelle
    setPosts(posts.map(p => 
      p.id === postId 
        ? { 
            ...p, 
            comments: [...(p.comments || []), newComment]
          }
        : p
    ));
    
    // Input'u temizle
    setCommentInputs({ ...commentInputs, [postId]: '' });

    try {
      const updatedPost = await postApi.addComment(postId, dietitianId, dietitianName, commentContent);
      // Gerçek yorumu al ve temp yorumu değiştir
      const realComment = updatedPost.comments?.find(c => 
        c.userId === dietitianId && 
        c.content === commentContent &&
        c.id !== tempCommentId
      );
      
      if (realComment) {
        setPosts(posts.map(p => 
          p.id === postId 
            ? { 
                ...p, 
                comments: p.comments?.map(c => 
                  c.id === tempCommentId ? realComment : c
                ) || []
              }
            : p
        ));
      } else {
        // Gerçek yorumu bulamazsak tüm postu güncelle
        setPosts(posts.map(p => 
          p.id === postId ? updatedPost : p
        ));
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      // Hata olursa geri al
      setPosts(posts.map(p => 
        p.id === postId 
          ? { 
              ...p, 
              comments: p.comments?.filter(c => c.id !== tempCommentId) || []
            }
          : p
      ));
      setCommentInputs({ ...commentInputs, [postId]: commentContent });
      showError('Yorum eklenirken hata oluştu');
    }
  };

  const handleDeleteComment = async (postId: string, commentId: string) => {
    // Silinecek yorumu sakla (geri alma için)
    const post = posts.find(p => p.id === postId);
    const commentToDelete = post?.comments?.find(c => c.id === commentId);
    
    if (!commentToDelete) return;

    // Optimistic update - önce UI'ı güncelle
    setPosts(posts.map(p => 
      p.id === postId 
        ? { 
            ...p, 
            comments: p.comments?.filter(c => c.id !== commentId) || []
          }
        : p
    ));

    try {
      await postApi.deleteComment(postId, commentId);
      // Başarılı oldu, zaten güncelledik
      setCommentMenuAnchor({ ...commentMenuAnchor, [commentId]: null });
    } catch (error) {
      console.error('Error deleting comment:', error);
      // Hata olursa geri al
      if (commentToDelete) {
        setPosts(posts.map(p => 
          p.id === postId 
            ? { 
                ...p, 
                comments: [...(p.comments || []), commentToDelete].sort((a, b) => 
                  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                )
              }
            : p
        ));
      }
      showError('Yorum silinirken hata oluştu');
    }
  };

  const handleStartEditComment = (commentId: string, currentContent: string) => {
    setEditingComments({ ...editingComments, [commentId]: currentContent });
    setCommentMenuAnchor({ ...commentMenuAnchor, [commentId]: null });
  };

  const handleCancelEditComment = (commentId: string) => {
    const newEditing = { ...editingComments };
    delete newEditing[commentId];
    setEditingComments(newEditing);
  };

  const handleSaveEditComment = async (postId: string, commentId: string) => {
    const newContent = editingComments[commentId]?.trim();
    if (!newContent) return;

    const post = posts.find(p => p.id === postId);
    const originalComment = post?.comments?.find(c => c.id === commentId);
    
    if (!originalComment) return;

    // Optimistic update
    setPosts(posts.map(p => 
      p.id === postId 
        ? { 
            ...p, 
            comments: p.comments?.map(c => 
              c.id === commentId ? { ...c, content: newContent } : c
            ) || []
          }
        : p
    ));

    // Düzenleme modunu kapat
    handleCancelEditComment(commentId);

    try {
      await postApi.updateComment(postId, commentId, newContent);
      // Başarılı oldu, zaten güncelledik
    } catch (error) {
      console.error('Error updating comment:', error);
      // Hata olursa geri al
      setPosts(posts.map(p => 
        p.id === postId 
          ? { 
              ...p, 
              comments: p.comments?.map(c => 
                c.id === commentId ? originalComment : c
              ) || []
            }
          : p
      ));
      showError('Yorum güncellenirken hata oluştu');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((t) => t !== tag) || [],
    });
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'article':
        return <ArticleIcon />;
      case 'news':
        return <NewspaperIcon />;
      case 'product':
        return <ShoppingBagIcon />;
      default:
        return <ArticleIcon />;
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'article':
        return 'Yazı';
      case 'news':
        return 'Haber';
      case 'product':
        return 'Ürün';
      default:
        return type;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Az önce';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dakika önce`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} gün önce`;
    
    return date.toLocaleDateString('tr-TR', {
      day: 'numeric',
      month: 'long',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  const isLiked = (post: Post) => {
    return post.likedBy?.includes(dietitianId || '') || false;
  };

  if (loading) {
    return (
      <Loading
        size="large"
        variant="wave"
        mode="inline"
        message="İçerikler yükleniyor..."
      />
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', pb: 4 }}>
      {/* Header - Instagram benzeri */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'white',
          borderBottom: '1px solid #efefef',
          borderRadius: 0,
          px: 2,
          py: 1.5,
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600} sx={{ color: '#262626' }}>
            İçeriklerim
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{
              bgcolor: colors.primary.main,
              color: 'white',
              textTransform: 'none',
              borderRadius: 2,
              px: 2,
              width:'fit-content',
              '&:hover': {
                bgcolor: colors.primary.dark,
              },
            }}
          >
            Paylaş
          </Button>
        </Box>
      </Paper>

      {/* Feed */}
      {posts.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px solid #efefef',
            bgcolor: '#fafafa',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Henüz içerik eklenmemiş
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenModal()}
            sx={{ mt: 2, bgcolor: colors.primary.main }}
          >
            İlk İçeriğinizi Ekleyin
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {posts.map((post) => (
            <Card
              key={post.id}
              elevation={0}
              sx={{
                border: '1px solid #efefef',
                borderRadius: 0,
                bgcolor: 'white',
              }}
            >
              {/* Post Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 1.5,
                  borderBottom: '1px solid #efefef',
                }}
              >
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Avatar
                    src={dietitianProfileImage}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: colors.primary.main,
                      fontSize: '0.875rem',
                    }}
                  >
                    {post.dietitianName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ color: '#262626' }}>
                      {dietitianName || post.dietitianName}
                    </Typography>
                    {!post.isPublished && (
                      <Typography variant="caption" sx={{ color: '#8e8e8e' }}>
                        Taslak
                      </Typography>
                    )}
                  </Box>
                </Box>
                <IconButton
                  size="small"
                  sx={{ width:'fit-content' }}
                  onClick={(e) => setMenuAnchor({ ...menuAnchor, [post.id]: e.currentTarget })}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor[post.id]}
                  open={Boolean(menuAnchor[post.id])}
                  onClose={() => setMenuAnchor({ ...menuAnchor, [post.id]: null })}
                >
                  <MenuItem onClick={() => { handleOpenModal(post); setMenuAnchor({ ...menuAnchor, [post.id]: null }); }}>
                    <EditIcon sx={{ mr: 1, fontSize: 18 }} /> Düzenle
                  </MenuItem>
                  <MenuItem onClick={() => { handleTogglePublish(post.id); setMenuAnchor({ ...menuAnchor, [post.id]: null }); }}>
                    {post.isPublished ? (
                      <>
                        <PublicOffIcon sx={{ mr: 1, fontSize: 18 }} /> Yayından Kaldır
                      </>
                    ) : (
                      <>
                        <PublicIcon sx={{ mr: 1, fontSize: 18 }} /> Yayınla
                      </>
                    )}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleDelete(post.id);
                      setMenuAnchor({ ...menuAnchor, [post.id]: null });
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Sil
                  </MenuItem>
                </Menu>
              </Box>

              {/* Post Image */}
              {post.imageUrl && (
                <CardMedia
                  component="img"
                  image={post.imageUrl}
                  alt={post.title}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    objectFit: 'cover',
                  }}
                />
              )}

              {/* Post Actions */}
              <Box sx={{ p: 1.5 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <IconButton
                    size="small"
                    onClick={() => handleToggleLike(post)}
                    sx={{ color: isLiked(post) ? '#ed4956' : '#262626', width:'fit-content' }}
                  >
                    {isLiked(post) ? (
                      <FavoriteIcon fontSize="medium" />
                    ) : (
                      <FavoriteBorderIcon fontSize="medium" />
                    )}
                  </IconButton>
                  <IconButton size="small" sx={{ color: '#262626', width:'fit-content' }}>
                    <CommentIcon fontSize="medium" />
                  </IconButton>
                  <IconButton size="small" sx={{ color: '#262626', width:'fit-content' }}>
                    <SendIcon fontSize="medium" />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }} />
                  <Chip
                    icon={getTypeIcon(post.type)}
                    label={getTypeLabel(post.type)}
                    size="small"
                    sx={{
                      height: 24,
                      fontSize: '0.75rem',
                      bgcolor: colors.alpha?.primary10 || '#e3f2fd',
                      color: colors.primary.main,
                    }}
                  />
                </Box>

                {/* Likes Count */}
                {post.likeCount && post.likeCount > 0 && (
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: '#262626' }}>
                    {post.likeCount} beğeni
                  </Typography>
                )}

                {/* Post Content */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" component="span" fontWeight={600} sx={{ color: '#262626', mr: 1 }}>
                    {dietitianName || post.dietitianName}
                  </Typography>
                  <Typography variant="body2" component="span" sx={{ color: '#262626' }}>
                    {post.content}
                  </Typography>
                </Box>

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    {post.tags.map((tag) => (
                      <Typography
                        key={tag}
                        variant="body2"
                        component="span"
                        sx={{ color: '#00376b', mr: 1, cursor: 'pointer' }}
                      >
                        #{tag}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Product Price */}
                {post.type === 'product' && post.price && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h6" fontWeight={700} sx={{ color: colors.primary.main }}>
                      ₺{post.price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                    </Typography>
                    {post.productUrl && (
                      <Button
                        size="small"
                        href={post.productUrl}
                        target="_blank"
                        sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                      >
                        Ürünü İncele →
                      </Button>
                    )}
                  </Box>
                )}

                {/* View Comments */}
                {post.comments && post.comments.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    {post.comments.length > 2 && (
                      <Typography
                        variant="body2"
                        sx={{ 
                          color: '#8e8e8e', 
                          cursor: 'pointer', 
                          mb: 0.5,
                          '&:hover': { color: '#262626' }
                        }}
                        onClick={() => setExpandedComments({ ...expandedComments, [post.id]: !expandedComments[post.id] })}
                      >
                        {expandedComments[post.id]
                          ? 'Daha az göster'
                          : `Tüm ${post.comments.length} yorumu gör`}
                      </Typography>
                    )}
                    {(expandedComments[post.id] ? post.comments : post.comments.slice(0, 2)).map((comment) => {
                      const isEditing = editingComments[comment.id] !== undefined;
                      const isOwnComment = comment.userId === dietitianId;
                      
                      return (
                        <Box key={comment.id} sx={{ mt: 0.5, display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                          {isEditing ? (
                            <Box sx={{ flex: 1, display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <TextField
                                fullWidth
                                size="small"
                                value={editingComments[comment.id]}
                                onChange={(e) =>
                                  setEditingComments({ ...editingComments, [comment.id]: e.target.value })
                                }
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEditComment(post.id, comment.id);
                                  } else if (e.key === 'Escape') {
                                    handleCancelEditComment(comment.id);
                                  }
                                }}
                                autoFocus
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    fontSize: '0.875rem',
                                    py: 0.5,
                                  },
                                }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleSaveEditComment(post.id, comment.id)}
                                sx={{ width: 'fit-content', color: colors.primary.main }}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleCancelEditComment(comment.id)}
                                sx={{ width: 'fit-content' }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" component="span" fontWeight={600} sx={{ color: '#262626', mr: 1 }}>
                                  {comment.userName}
                                </Typography>
                                <Typography variant="body2" component="span" sx={{ color: '#262626' }}>
                                  {comment.content}
                                </Typography>
                              </Box>
                              {isOwnComment && (
                                <IconButton
                                  size="small"
                                  onClick={(e) => setCommentMenuAnchor({ ...commentMenuAnchor, [comment.id]: e.currentTarget })}
                                  sx={{ width: 'fit-content', p: 0.5 }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                              )}
                              <Menu
                                anchorEl={commentMenuAnchor[comment.id]}
                                open={Boolean(commentMenuAnchor[comment.id])}
                                onClose={() => setCommentMenuAnchor({ ...commentMenuAnchor, [comment.id]: null })}
                              >
                                <MenuItem onClick={() => handleStartEditComment(comment.id, comment.content)}>
                                  <EditIcon sx={{ mr: 1, fontSize: 18 }} /> Düzenle
                                </MenuItem>
                                <MenuItem
                                  onClick={() => {
                                    handleDeleteComment(post.id, comment.id);
                                    setCommentMenuAnchor({ ...commentMenuAnchor, [comment.id]: null });
                                  }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon sx={{ mr: 1, fontSize: 18 }} /> Sil
                                </MenuItem>
                              </Menu>
                            </>
                          )}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* Time */}
                <Typography variant="caption" sx={{ color: '#8e8e8e', textTransform: 'uppercase' }}>
                  {formatTimeAgo(post.createdAt)}
                </Typography>
              </Box>

              {/* Comment Input */}
              <Box
                sx={{
                  borderTop: '1px solid #efefef',
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Yorum ekle..."
                  value={commentInputs[post.id] || ''}
                  onChange={(e) =>
                    setCommentInputs({ ...commentInputs, [post.id]: e.target.value })
                  }
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddComment(post.id);
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => handleAddComment(post.id)}
                          disabled={!commentInputs[post.id]?.trim()}
                          sx={{ color: colors.primary.main, width:'fit-content' }}
                        >
                          <SendIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '& fieldset': {
                        border: 'none',
                      },
                    },
                  }}
                />
              </Box>
            </Card>
          ))}
        </Box>
      )}

      {/* Add/Edit Modal */}
      <Dialog
        open={showModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h5" fontWeight={600}>
              {editingPost ? 'İçeriği Düzenle' : 'Yeni Paylaşım'}
            </Typography>
            <IconButton onClick={handleCloseModal} sx={{ width:'fit-content' }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {/* Content Type */}
            <Box mb={3}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                İçerik Tipi *
              </Typography>
              <ToggleButtonGroup
                value={formData.type}
                exclusive
                onChange={(_, value) =>
                  value && setFormData({ ...formData, type: value })
                }
                fullWidth
              >
                <ToggleButton value="article">
                  <ArticleIcon sx={{ mr: 1 }} />
                  Yazı
                </ToggleButton>
                <ToggleButton value="news">
                  <NewspaperIcon sx={{ mr: 1 }} />
                  Haber
                </ToggleButton>
                <ToggleButton value="product">
                  <ShoppingBagIcon sx={{ mr: 1 }} />
                  Ürün
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Title */}
            <TextField
              fullWidth
              label="Başlık"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              required
              sx={{ mb: 2 }}
            />

            {/* Content */}
            <TextField
              fullWidth
              label="İçerik"
              value={formData.content}
              onChange={(e) =>
                setFormData({ ...formData, content: e.target.value })
              }
              multiline
              rows={6}
              required
              sx={{ mb: 2 }}
            />

            {/* Image URL */}
            <TextField
              fullWidth
              label="Görsel URL"
              value={formData.imageUrl}
              onChange={(e) =>
                setFormData({ ...formData, imageUrl: e.target.value })
              }
              placeholder="https://example.com/image.jpg"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ImageIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />

            {/* Product Fields */}
            {formData.type === 'product' && (
              <>
                <TextField
                  fullWidth
                  label="Fiyat (₺)"
                  type="number"
                  value={formData.price || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      price: e.target.value
                        ? parseFloat(e.target.value)
                        : undefined,
                    })
                  }
                  inputProps={{ step: '0.01' }}
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Ürün Linki"
                  value={formData.productUrl}
                  onChange={(e) =>
                    setFormData({ ...formData, productUrl: e.target.value })
                  }
                  placeholder="https://example.com/product"
                  sx={{ mb: 2 }}
                />
              </>
            )}

            {/* Tags */}
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Etiketler
              </Typography>
              <Box display="flex" gap={1} mb={1}>
                <TextField
                  fullWidth
                  size="small"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) =>
                    e.key === 'Enter' && (e.preventDefault(), handleAddTag())
                  }
                  placeholder="Etiket ekle"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TagIcon sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button 
                  variant="outlined" 
                  onClick={handleAddTag}
                  sx={{
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    '&:hover': {
                      borderColor: colors.primary.dark,
                      bgcolor: colors.primary.main,
                      color: 'white',
                    },
                  }}
                >
                  Ekle
                </Button>
              </Box>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {formData.tags?.map((tag) => (
                  <Chip
                    key={tag}
                    label={`#${tag}`}
                    onDelete={() => handleRemoveTag(tag)}
                    variant="outlined"
                    sx={{
                      borderColor: colors.primary.main,
                      color: colors.primary.main,
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Görünürlük: Herkese açık / Sadece danışanlarıma */}
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                Kimler görsün?
              </Typography>
              <ToggleButtonGroup
                value={formData.visibility || 'public'}
                exclusive
                onChange={(_, value) =>
                  value && setFormData({ ...formData, visibility: value })
                }
                fullWidth
              >
                <ToggleButton value="public">
                  <PublicIcon sx={{ mr: 1 }} />
                  Herkese Açık
                </ToggleButton>
                <ToggleButton value="clients_only">
                  <GroupIcon sx={{ mr: 1 }} />
                  Sadece Danışanlarıma
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Publish Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isPublished}
                  onChange={(e) =>
                    setFormData({ ...formData, isPublished: e.target.checked })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.primary.main,
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.primary.main,
                    },
                  }}
                />
              }
              label="Hemen yayınla"
            />
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <CancelButton onClick={handleCloseModal} />
            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                '&:hover': {
                  bgcolor: colors.primary.dark,
                },
              }}
            >
              {editingPost ? 'Güncelle' : 'Paylaş'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default ContentManagementPage;
